import {
  createEffect,
  createMemo,
  useContext,
  createContext,
  createSignal,
  onCleanup,
  Show,
  batch,
  onMount,
  For,
  Switch,
  Match,
} from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/+esm";
import { createStore } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/store/+esm";
import { render } from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/web/+esm";
import html from "https://cdn.jsdelivr.net/npm/solid-js@1.9.5/html/+esm";
import { animate } from "https://cdn.jsdelivr.net/npm/motion@11.15.0/+esm";
import {
  HashRouter,
  useLocation,
  useNavigate,
  A,
} from "https://cdn.jsdelivr.net/npm/@brytdesigns/fork-solid-router@0.15.4/+esm";

/*
 * Quiz structure
 *
 *
 * 1. Question:
 * fields:
 *  - "group" - the grouping the question is associated with
 *  - "label" - the actual question being asking
 *  - "type" - the type of answer a question is looking for, IE: "multi", "radio", "input", "timed", "select"
 *     - "multi" -> checkbox style questions, meaning multiple options can be selected
 *     - "single" -> single radio style questions, meaning only one option can be selected
 *     - "text-input" -> a custom input provided by the customer, meaning no pre defined options
 *     - "timed" -> no answers/just a way to provide the user with information
 *     - "select" -> A select style question where there are maybe too many answers.
 *     - "submit" -> The final slide before the quiz is "submitted" to the results page.
 *
 * optional fields based on type:
 *   - "multi"
 *     - "optionName"
 *     - "options" -> Array of options
 *     - "maxAllowed" -> Number representing the maximum allowed options to be selected
 *
 *   - "single"
 *    - "optionName"
 *    - "options" -> Array of options
 *
 *   - "text-input"
 *    - "field" -> A field object containing pertinant information about the field
 *
 *   - "timed"
 *    - "content" -> Object containing details about the field
 *    - "duration" -> How long before switching to the next slide
 *
 *   - "select"
 *    - "options" -> Array of select options
 *
 *
 *  2. Field
 *  fields:
 *    - "label" -> The readable name of the input
 *    - "name" -> The name of the input
 *
 *  3. Option
 *  fields:
 *    - "type" -> "radio" | "checkbox"
 *    - "icon" -> Icon object
 *      - "type" -> "html" | "url"
 *      - "content" -> Represent the url or the html
 *    - "variant" -> "default" | "square"
 *    - "label" -> The readable name of the option
 *    - "value" -> The actual value of the option
 *
 *  4. Select Option
 *  fields:
 *    - "label" -> Readable name
 *    - "value"
 *    - "selected" -> Boolean value
 *    - "disabled" -> Boolean value
 *
 *  5. Page Content
 *  fields:
 *   - "image"
 *   - "copy"
 *
 */

const MetaContext = createContext(null);

function useMeta() {
  const context = useContext(MetaContext);
  if (!context)
    throw new Error("BundleContext must be used within a BundleProvider");
  return context;
}

function MetaProvider(props) {
  const url = new URL(import.meta.url);
  const script = document.querySelector(
    `script[src*="${url.pathname + url.search}"]`,
  );
  if (!script)
    return console.warn(
      `Unable to find the script tag with the provided URL. Skipping script execution.`,
    );
  const sectionId = script.getAttribute("data-section-id");
  if (!sectionId)
    return console.warn(
      `No section ID provided (data-section-id). Skipping script execution.`,
    );
  const section = document.getElementById(`shopify-section-${sectionId}`);
  if (!section)
    return console.warn(
      `Unable to find the section with the provided ID. Skipping script execution.`,
    );

  const metaData = () => {
    try {
      const script = section.querySelector('script[type="application/json"]');
      if (!script) return null;
      return JSON.parse(script.textContent);
    } catch (error) {
      console.warn(error);
      return null;
    }
  };

  return html`
    <${MetaContext.Provider} value=${metaData()}> ${() => props.children} <//>
  `;
}

// const STATES = [
//   { value: "al", label: "Alabama" },
//   { value: "ak", label: "Alaska" },
//   { value: "az", label: "Arizona" },
//   { value: "ar", label: "Arkansas" },
//   { value: "ca", label: "California" },
//   { value: "co", label: "Colorado" },
//   { value: "ct", label: "Connecticut" },
//   { value: "de", label: "Delaware" },
//   { value: "fl", label: "Florida" },
//   { value: "ga", label: "Georgia" },
//   { value: "hi", label: "Hawaii" },
//   { value: "id", label: "Idaho" },
//   { value: "il", label: "Illinois" },
//   { value: "in", label: "Indiana" },
//   { value: "ia", label: "Iowa" },
//   { value: "ks", label: "Kansas" },
//   { value: "ky", label: "Kentucky" },
//   { value: "la", label: "Louisiana" },
//   { value: "me", label: "Maine" },
//   { value: "md", label: "Maryland" },
//   { value: "ma", label: "Massachusetts" },
//   { value: "mi", label: "Michigan" },
//   { value: "mn", label: "Minnesota" },
//   { value: "ms", label: "Mississippi" },
//   { value: "mo", label: "Missouri" },
//   { value: "mt", label: "Montana" },
//   { value: "ne", label: "Nebraska" },
//   { value: "nv", label: "Nevada" },
//   { value: "nh", label: "New Hampshire" },
//   { value: "nj", label: "New Jersey" },
//   { value: "nm", label: "New Mexico" },
//   { value: "ny", label: "New York" },
//   { value: "nc", label: "North Carolina" },
//   { value: "nd", label: "North Dakota" },
//   { value: "oh", label: "Ohio" },
//   { value: "ok", label: "Oklahoma" },
//   { value: "or", label: "Oregon" },
//   { value: "pa", label: "Pennsylvania" },
//   { value: "ri", label: "Rhode Island" },
//   { value: "sc", label: "South Carolina" },
//   { value: "sd", label: "South Dakota" },
//   { value: "tn", label: "Tennessee" },
//   { value: "tx", label: "Texas" },
//   { value: "ut", label: "Utah" },
//   { value: "vt", label: "Vermont" },
//   { value: "va", label: "Virginia" },
//   { value: "wa", label: "Washington" },
//   { value: "wv", label: "West Virginia" },
//   { value: "wi", label: "Wisconsin" },
//   { value: "wy", label: "Wyoming" },
// ];

const QUESTION_TYPE = {
  MULTIPLE: "multiple",
  SINGLE: "single",
  TEXT_INPUT: "text-input",
  TIMED: "timed",
  SELECT: "select",
  SUBMIT: "submit",
  DYNAMIC: "dynamic",
};

const GROUP = {
  BASICS: {
    handle: "basics",
    label: "Basics",
  },
  LIFESTYLE: {
    handle: "lifestyle",
    label: "Lifestyle",
  },
  GOALS: {
    handle: "goals",
    label: "Goals",
  },
  SLEEP: {
    handle: "sleep",
    label: "Sleep",
  },
  MOOD_STRESS: {
    handle: "mood-stress",
    label: "Mood",
  },
  HAIR_SKIN_NAILS: {
    handle: "hair-skin-nails",
    label: "Hair, Skin, & Nails",
  },
  BRAIN_COGNITION: {
    handle: "brain-cognition",
    label: "Brain & Cognition",
  },
  FITNESS: {
    handle: "fitness",
    label: "Fitness",
  },
  ENERGY: {
    handle: "energy",
    label: "Energy",
  },
  IMMUNITY: {
    handle: "immunity",
    label: "Immunity",
  },
  BONE_JOINT: {
    handle: "bone-joint",
    label: "Bone + joint health",
  },
  GUT_HEALTH: {
    handle: "gut-health",
    label: "Gut health",
  },
  SUBMIT: {
    handle: "submit",
    label: "Submit",
  },
  METABOLISM: {
    handle: "metabolism-weight-loss",
    label: "Metabolism & Weight Loss",
  },
};

const QUESTIONS = [
  {
    answered: false,
    handle: "name",
    group: GROUP.BASICS,
    label: "What's your first name?",
    type: QUESTION_TYPE.TEXT_INPUT,
    show_disclaimer: true,
    field: {
      label: "First Name",
      name: "first_name",
      value: "",
    },
  },
  {
    answered: false,
    handle: "greeting",
    variant: "icon",
    group: GROUP.BASICS,
    label: "Hi, {basics.first_name.values.0.value}<br />It's nice to meet you!",
    type: QUESTION_TYPE.TIMED,
    duration: 2000,
    content: {
      image: `<svg id="Layer_2" style="width:120px;height:120px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 265.35 188.92"><defs><style>.cls-1{fill:none;stroke:#301d01;stroke-linecap:round;stroke-linejoin:round;stroke-width:9.02px;}.cls-2{fill:#301d01;}.cls-2,.cls-3{stroke-width:0px;}.cls-3{fill:#eceaca;}</style></defs><g id="Layer_1-2"><path class="cls-2" d="M6.95,176.73c-1.02,0-2.06-.08-3.11-.24-2.46-.37-4.16-2.67-3.78-5.14.37-2.46,2.67-4.17,5.14-3.78,7.65,1.15,15.06-5.66,17.8-12.92,3.22-8.54,2.28-18.66,1.45-27.58-.23-2.48,1.59-4.68,4.07-4.91,2.48-.23,4.68,1.59,4.91,4.07.92,9.9,1.96,21.11-1.99,31.6-3.62,9.6-13.18,18.89-24.48,18.89Z"/><path class="cls-3" d="M103.49,0C51.32,0,9.03,42.29,9.03,94.46s42.29,94.46,94.46,94.46c25.41,0,48.47-10.03,65.44-26.35,3.28-3.81,6.57-7.62,9.85-11.43-9.69-9.24-18.84-19.76-22.72-32.58-.68-2.26-1.2-4.67-.65-6.97,1.53-6.35,10.59-8.07,16.16-4.64,5.56,3.43,8.37,9.87,10.86,15.92.88-11.59,1.79-23.36,5.78-34.27,1.36-3.72,3.14-7.42,6.09-10.06.62-.56,1.31-1.06,2.03-1.49C188.16,33.19,149.71,0,103.49,0Z"/><path class="cls-1" d="M44.31,86.73c0-8.58,6.95-15.53,15.53-15.53s15.53,6.95,15.53,15.53"/><path class="cls-1" d="M161.07,86.73c0-8.58-6.95-15.53-15.53-15.53s-15.53,6.95-15.53,15.53"/><path class="cls-1" d="M66.16,135.84c20.43,19.42,52.73,18.6,72.15-1.83"/><polyline class="cls-1" points="102.24 75.31 96.52 116.4 114 116.4"/><path class="cls-2" d="M167.61,168.62c-1.04,0-2.09-.36-2.94-1.09-1.89-1.63-2.1-4.47-.47-6.36l8.41-9.76c-8.09-8.07-16.93-18.59-20.85-31.54-.77-2.54-1.54-5.89-.71-9.33.93-3.88,3.65-6.93,7.66-8.59,4.86-2.01,10.85-1.55,15.25,1.16,2.09,1.29,3.85,2.89,5.35,4.67.86-6.97,2.21-13.95,4.69-20.73,1.34-3.67,3.39-8.34,7.31-11.87,4.46-4.02,10.58-5.41,15.57-3.55,6.37,2.37,10.18,9.17,10.46,18.67.23,7.89-1.23,15.92-4.23,23.22-.95,2.3-3.58,3.4-5.89,2.46-2.3-.95-3.41-3.58-2.46-5.89,2.52-6.14,3.75-12.9,3.55-19.53-.13-4.29-1.4-9.29-4.59-10.48-1.83-.68-4.46.06-6.39,1.81-1.91,1.71-3.41,4.26-4.87,8.26-3.81,10.41-4.68,21.93-5.52,33.06-.16,2.08-1.73,3.79-3.79,4.12-2.06.32-4.08-.81-4.88-2.74-2.47-6.02-4.86-11.21-9.05-13.79-1.96-1.21-4.87-1.41-7.08-.5-1.3.54-2.08,1.33-2.33,2.36-.32,1.33.14,3.16.58,4.61,3.81,12.61,13.5,22.99,21.51,30.62,1.74,1.66,1.87,4.39.3,6.21l-11.18,12.97c-.89,1.04-2.15,1.57-3.42,1.57Z"/><path class="cls-2" d="M201.96,184.18c-.84,0-1.68-.23-2.44-.72-2.1-1.35-2.7-4.14-1.35-6.24l9.82-15.27c1.07-1.66,3.08-2.43,4.98-1.91,10.22,2.79,22.24,1.38,35.72-4.17,2.96-1.22,6.48-2.95,7.41-5.51.49-1.34.08-2.68-.35-3.58-.94-1.97-2.91-3.7-5-4.41-4.45-1.52-9.75-.37-14.82,1.1-2.24.65-4.6-.51-5.45-2.68-.85-2.17.08-4.63,2.16-5.68,8.41-4.25,15.68-11.05,20.49-19.15,1.81-3.05,1.41-4.03,1.36-4.13-.03-.06-.31-.56-1.67-1.15-3.9-1.68-9.16-1.11-14.06,1.53-4.93,2.65-9.16,6.94-13.24,11.09-1.65,1.67-4.3,1.8-6.1.3-1.8-1.5-2.15-4.14-.8-6.06,4.74-6.74,8.91-13.93,12.4-21.39,1.83-3.91,3.41-8,2.6-11.48-.08-.35-.16-.55-.2-.64-.47-.11-1.78.07-3.16.85-7.18,4.06-11,12.36-14.92,21.98-.94,2.31-3.57,3.42-5.88,2.48-2.31-.94-3.42-3.57-2.48-5.88,4.4-10.81,9.14-20.96,18.84-26.44,4.58-2.59,10.84-3.22,14.5,1.18.98,1.18,1.66,2.63,2.08,4.43,1.43,6.15-.7,11.97-3.22,17.35-.42.91-.86,1.81-1.3,2.71,6.4-2.53,12.99-2.66,18.51-.28,2.94,1.27,5.02,3.08,6.17,5.4,1.79,3.58,1.23,7.88-1.67,12.77-2.72,4.58-6.11,8.81-9.99,12.53.93.18,1.85.43,2.76.74,4.4,1.5,8.22,4.88,10.22,9.05,1.66,3.46,1.91,7.21.69,10.55-2.13,5.88-7.78,8.85-12.45,10.77-14.02,5.78-26.88,7.56-38.28,5.3l-8.1,12.59c-.86,1.34-2.31,2.07-3.8,2.07Z"/><path class="cls-2" d="M195.15,148.54c-.78,0-1.57-.2-2.29-.63-2.14-1.27-2.85-4.04-1.59-6.18.93-1.56,1.24-3.48.86-5.26-.38-1.78-1.44-3.4-2.92-4.46-2.03-1.45-2.5-4.26-1.05-6.29,1.45-2.03,4.26-2.5,6.29-1.05,3.3,2.35,5.67,5.97,6.51,9.94.84,3.96.14,8.24-1.93,11.72-.84,1.42-2.34,2.21-3.89,2.21Z"/><path class="cls-2" d="M181.36,58.53c-9.67,0-19.82-2.28-29.75-6.78-15.55-7.04-28.55-18.54-38.14-27.77-1.79-1.73-1.85-4.58-.12-6.38,1.73-1.79,4.58-1.85,6.38-.12,9.07,8.73,21.31,19.58,35.61,26.06,14.34,6.49,29.01,7.74,41.31,3.51,2.35-.81,4.92.44,5.73,2.8.81,2.36-.44,4.92-2.8,5.73-5.74,1.97-11.87,2.95-18.21,2.95Z"/><path class="cls-2" d="M51.15,57.12c-14.67,0-28.87-4.26-40.09-12.35-2.02-1.46-2.48-4.28-1.02-6.3,1.46-2.02,4.28-2.48,6.3-1.02,13.74,9.9,32.76,13.15,50.87,8.7,16.39-4.02,32.37-14.31,45-28.96,1.63-1.89,4.48-2.1,6.36-.47,1.89,1.63,2.1,4.48.47,6.36-13.84,16.06-31.49,27.36-49.68,31.83-6.03,1.48-12.16,2.21-18.21,2.21Z"/></g></svg>`,
    },
  },
  {
    answered: false,
    group: GROUP.BASICS,
    handle: "age",
    label: "How old are you?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "age",
    options: [
      {
        type: "radio",
        variant: "default",
        value: "18-24",
      },
      {
        type: "radio",
        variant: "default",
        value: "25-34",
      },
      {
        type: "radio",
        variant: "default",
        value: "35-44",
      },
      {
        type: "radio",
        variant: "default",
        value: "45-54",
      },
      {
        type: "radio",
        variant: "default",
        value: "55-64",
      },
      {
        type: "radio",
        variant: "default",
        value: "65+",
      },
    ],
  },
  // {
  //   answered: false,
  //   handle: "state",
  //   group: GROUP.BASICS,
  //   label: "Which state do you live in?",
  //   type: QUESTION_TYPE.SELECT,
  //   optionName: "state",
  //   options: STATES,
  // },
  {
    answered: false,
    handle: "dietary-preferences",
    group: GROUP.BASICS,
    label: "What are your dietary preferences/restrictions?",
    type: QUESTION_TYPE.MULTIPLE,
    optionName: "dietary_preferences",
    min: 1,
    max: Infinity,
    options: [
      {
        variant: "default",
        value: "vegan",
        label: "Vegan",
      },
      {
        variant: "default",
        label: "Gluten-free",
        value: "gluten-free",
      },
      {
        variant: "default",
        value: "vegetarian",
        label: "Vegetarian",
      },
      {
        variant: "default",
        value: "pescatarian",
        label: "Pescatarian",
      },
      {
        variant: "default",
        value: "dairy-free",
        label: "Dairy-free",
      },
      {
        variant: "default",
        value: "nut-free",
        label: "Nut-free",
      },
      {
        variant: "default",
        value: "none",
        label: "None",
      },
    ],
  },
  {
    answered: false,
    handle: "preference",
    group: GROUP.BASICS,
    label: "Do you have a preference on how you get your vitamins?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "preference",
    options: [
      {
        variant: "default",
        value: "pill",
        label: "Pill",
      },
      {
        variant: "default",
        value: "capsule",
        label: "Capsule",
      },
      {
        variant: "default",
        value: "real-food",
        label: "Real food",
      },
      {
        variant: "default",
        value: "tablet",
        label: "Tablet",
      },
      {
        variant: "default",
        value: "gummy-bear",
        label: "Gummy bear",
      },
      {
        variant: "default",
        value: "none",
        label: "None",
      },
    ],
  },
  {
    answered: false,
    handle: "optimal-absorption",
    group: GROUP.BASICS,
    label: "Let's talk about optimal absorption",
    type: QUESTION_TYPE.TIMED,
    // duration: 4000,
    content: {
      image: `<img
            class="object-cover w-full h-full mb-6 rounded-xl"
            src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/0dd0b91ae414516e09b0a0edbe3bb126.jpg?v=1740598653"
            alt=""
          >`,
      copy: "Why is real food in your supplements better? GEM Bites are crafted from whole, plant-based foods, ensuring that nutrients are in their most bioavailable form. This means your body can absorb vitamins and minerals more efficiently compared to traditional supplements. Pills and capsules often contain isolated nutrients that aren't as easily absorbed, leaving much of their potential benefit untapped. Choose GEM Bites for a more effective way to boost your daily intake.",
    },
  },
  {
    answered: false,
    handle: "biggest-challenge",
    group: GROUP.BASICS,
    label:
      "What is your biggest challenge with maintaining a consistent supplement intake?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "biggest_challenge",
    options: [
      {
        variant: "default",
        value: "remembering",
        label: "Remembering to take them",
      },
      {
        variant: "default",
        value: "cost",
        label: "Cost",
      },
      {
        variant: "default",
        value: "effectiveness",
        label: "Perceived lack of effectiveness",
      },
      {
        variant: "default",
        value: "taste",
        label: "Taste or method of ingestion",
      },
      {
        variant: "default",
        value: "none",
        label: "None",
      },
    ],
  },
  {
    answered: false,
    handle: "live",
    group: GROUP.LIFESTYLE,
    label: "Which option best describes where you live?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "live",
    options: [
      {
        variant: "square",
        value: "city",
        label: "City",
        icon: `<svg id="Layer_2" style="width:50px;height:50px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 211.33 202.89"><defs><style>.cls-1{fill:none;stroke:#301d01;stroke-linecap:round;stroke-linejoin:round;stroke-width:6.5px;}.cls-2{fill:#301d01;stroke-width:0px;}</style></defs><g id="Layer_1-2"><polyline class="cls-1" points="119.31 118.13 119.31 86.95 175.86 86.95 175.86 199.64"/><rect class="cls-1" x="128.72" y="71.48" width="37.73" height="14.52"/><rect class="cls-1" x="135.91" y="58.94" width="23.36" height="12.54"/><line class="cls-1" x1="3.25" y1="199.64" x2="208.07" y2="199.64"/><polyline class="cls-1" points="87.89 199.64 32.8 199.64 32.8 64.45 87.89 64.45 87.89 118.13"/><rect class="cls-1" x="40.59" y="42.76" width="39.5" height="21.7"/><rect class="cls-1" x="48.25" y="23.37" width="24.19" height="19.39"/><line class="cls-1" x1="60.35" y1="23.37" x2="60.35" y2="3.25"/><rect class="cls-1" x="65.3" y="118.13" width="75.82" height="81.5"/><rect class="cls-1" x="83.96" y="175.64" width="38.5" height="24"/><line class="cls-1" x1="103.21" y1="175.64" x2="103.21" y2="198.76"/><rect class="cls-2" x="77.18" y="132.71" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="98.7" y="132.71" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="120.22" y="132.71" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="77.18" y="152.81" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="98.7" y="152.81" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="120.22" y="152.81" width="9.01" height="9.01" rx="1.14" ry="1.14"/><rect class="cls-2" x="158.51" y="96.96" width="6.51" height="93.13" rx="3.25" ry="3.25"/><rect class="cls-2" x="130.15" y="96.96" width="6.51" height="22.83" rx="3.25" ry="3.25"/><rect class="cls-2" x="43.81" y="74.63" width="6.51" height="114.83" rx="3.25" ry="3.25"/><rect class="cls-2" x="70.38" y="74.63" width="6.51" height="43.54" rx="3.25" ry="3.25"/></g></svg>`,
      },
      {
        variant: "square",
        value: "suburbs",
        label: "Suburbs",
        icon: `<svg id="Layer_2" style="width:50px;height:50px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 226.2 204.21"><defs><style>.cls-1{fill:none;stroke:#301d01;stroke-linecap:round;stroke-linejoin:round;stroke-width:6.5px;}.cls-2{fill:#301d01;stroke-width:0px;}</style></defs><g id="Layer_1-2"><line class="cls-1" x1="188.35" y1="165.18" x2="188.35" y2="90.96"/><line class="cls-1" x1="24.59" y1="90.96" x2="24.59" y2="198.61"/><line class="cls-1" x1="4.05" y1="200.65" x2="208.88" y2="200.65"/><path class="cls-1" d="M4.86,99.06h0c2.4,2.8,6.61,3.12,9.4.73L106.47,20.81l92.21,78.97c2.8,2.4,7.01,2.07,9.4-.73h0c2.4-2.8,2.07-7.01-.73-9.4L106.47,3.25,5.58,89.65c-2.8,2.4-3.12,6.61-.73,9.4Z"/><polyline class="cls-1" points="147.83 37.48 147.83 3.4 171.44 3.4 171.44 57.8"/><polyline class="cls-1" points="82.87 198.6 82.87 103.73 130.06 103.73 130.06 198.6"/><ellipse class="cls-2" cx="116" cy="155.46" rx="3.53" ry="4.48"/><rect class="cls-1" x="38.25" y="103.73" width="29.88" height="36.74"/><rect class="cls-1" x="144.8" y="103.73" width="29.88" height="36.74" transform="translate(319.49 244.19) rotate(-180)"/><path class="cls-1" d="M162.88,200.96c-3.51-.85-6.97-2.45-9.26-5.24s-3.16-6.93-1.44-10.1,6.35-4.63,9.22-2.44c-.37-4.38,2.1-8.88,6-10.91s9-1.49,12.38,1.32c1.68-5.39,7.53-9.13,13.13-8.4s10.29,5.85,10.53,11.49c3.83-3.3,12.39-.91,14.27.53,10.81,8.28,3.23,23.52-7.8,23.52"/></g></svg>`,
      },
      {
        variant: "square",
        value: "rural",
        label: "Rural area",
        icon: `<svg id="Layer_2" style="width:50px;height:50px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 212.64 202.21"><defs><style>.cls-1{stroke-width:6.5px;}.cls-1,.cls-2{fill:none;stroke:#301d01;stroke-linecap:round;stroke-linejoin:round;}.cls-2{stroke-width:5.35px;}</style></defs><g id="Layer_1-2"><line class="cls-1" x1="4.57" y1="198.95" x2="209.39" y2="198.95"/><path class="cls-1" d="M198.5,198.95l-1.29-37.37-4.62-133.66c0-13.63-11.05-24.68-24.68-24.68s-24.68,11.05-24.68,24.68l-.29,8.32v10.31"/><line class="cls-1" x1="143.23" y1="37.78" x2="192.59" y2="37.78"/><polyline class="cls-1" points="39.43 111.06 47.03 198.95 106.22 198.95 165.41 198.95 173.01 111.06"/><rect class="cls-1" x="78.31" y="134.55" width="55.91" height="62.91"/><line class="cls-1" x1="134.22" y1="134.55" x2="78.31" y2="197.46"/><line class="cls-1" x1="78.22" y1="134.55" x2="134.13" y2="197.46"/><line class="cls-2" x1="43.75" y1="161.02" x2="2.67" y2="161.02"/><line class="cls-2" x1="45.39" y1="179.99" x2="2.67" y2="179.99"/><line class="cls-2" x1="12.79" y1="198.95" x2="12.79" y2="155.8"/><line class="cls-2" x1="168.69" y1="161.02" x2="207.5" y2="161.02"/><line class="cls-2" x1="167.05" y1="179.99" x2="207.5" y2="179.99"/><line class="cls-2" x1="197.39" y1="198.95" x2="197.39" y2="155.8"/><circle class="cls-1" cx="106.18" cy="89.16" r="11.87"/><path class="cls-1" d="M174.51,113.34l-39.68-60.13-28.61-13.61-28.61,13.61-39.68,60.13c-2.85,4.32-9.57,2.3-9.57-2.87h0c0-1.02.3-2.02.86-2.87l41.28-62.56,35.72-16.99,35.72,16.99,41.28,62.56c.56.85.86,1.85.86,2.87h0c0,5.17-6.72,7.19-9.57,2.87Z"/></g></svg>`,
      },
      {
        variant: "square",
        value: "other",
        label: "Other",
        icon: `<svg id="Layer_2" style="width:50px;height:50px;" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 159.42 205.83"><defs><style>.cls-1{fill:none;stroke:#301d01;stroke-linecap:round;stroke-linejoin:round;stroke-width:5.67px;}.cls-2{fill:#301d01;stroke-width:0px;}</style></defs><g id="Layer_1-2"><path class="cls-2" d="M114.22,143.8c-5.02,0-5.03,7.8,0,7.8s5.03-7.8,0-7.8Z"/><path class="cls-2" d="M80.54,173.14c-6.43,0-6.45,10,0,10s6.45-10,0-10Z"/><path class="cls-2" d="M21.17,121.26c-4.02,0-4.03,6.25,0,6.25s4.03-6.25,0-6.25Z"/><path class="cls-2" d="M21.17,139.65c-4.02,0-4.03,6.25,0,6.25s4.03-6.25,0-6.25Z"/><path class="cls-2" d="M36.63,143.8c-6.43,0-6.45,10,0,10s6.45-10,0-10Z"/><path class="cls-1" d="M121.39,84.91c7.58,8.07,13.39,18.07,16.6,29.51,10.44,37.29-10.76,75.82-47.35,86.06-36.59,10.24-74.71-11.68-85.15-48.97-7.9-28.24,2.34-57.19,23.66-74.03"/><polyline class="cls-1" points="124.5 37.36 118.17 134.1 33.28 134.49 26.04 37.82"/><polyline class="cls-1" points="33.8 30.96 33.67 3.87 44.82 3.81 44.91 22.99"/><path class="cls-1" d="M75.59,78.13h0c10.05,0,18.21,8.16,18.21,18.21v37.95h-36.42v-37.95c0-10.05,8.16-18.21,18.21-18.21Z" transform="translate(-.49 .35) rotate(-.27)"/><circle class="cls-1" cx="75.29" cy="47.43" r="11.47"/><circle class="cls-2" cx="102.75" cy="170.26" r="7.7"/><line class="cls-1" x1="9.51" y1="49.52" x2="74.26" y2="2.83"/><line class="cls-1" x1="140.04" y1="48.05" x2="74.26" y2="2.83"/><path class="cls-2" d="M136.73,7.79c-.99,5.85-4.48,10.45-8.91,11.77-.31.09-.31.67,0,.76,4.43,1.31,7.91,5.92,8.91,11.77.07.41.5.41.57,0,.99-5.85,4.48-10.45,8.91-11.77.31-.09.31-.67,0-.76-4.43-1.31-7.91-5.92-8.91-11.77-.07-.41-.5-.41-.57,0Z"/><path class="cls-2" d="M152.11,23.24c-.75,4.4-3.37,7.87-6.71,8.86-.23.07-.23.5,0,.57,3.33.99,5.96,4.46,6.71,8.86.05.31.38.31.43,0,.75-4.4,3.37-7.87,6.71-8.86.23-.07.23-.5,0-.57-3.33-.99-5.96-4.46-6.71-8.86-.05-.31-.38-.31-.43,0Z"/></g></svg>`,
      },
    ],
  },
  // {
  //   answered: false,
  //   handle: "currently-taking-vitamins",
  //   group: GROUP.LIFESTYLE,
  //   label: "Do you currently take vitamins?",
  //   type: QUESTION_TYPE.SINGLE,
  //   optionName: "currently_taking_vitamins",
  //   options: [
  //     {
  //       variant: "square",
  //       value: "yes",
  //       label: "Yes",
  //     },
  //     {
  //       variant: "square",
  //       value: "no",
  //       label: "No",
  //     },
  //   ],
  // },
  // {
  //   answered: false,
  //   handle: "current-supplements",
  //   group: GROUP.LIFESTYLE,
  //   label: "What kind of supplements do you take?",
  //   type: QUESTION_TYPE.MULTIPLE,
  //   optionName: "current_supplements",
  //   min: 1,
  //   max: Infinity,
  //   options: [
  //     {
  //       variant: "default",
  //       value: "biotin",
  //       label: "Biotin",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-b-complex",
  //       label: "Vitamin B complex",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-d",
  //       label: "Vitamin D",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-c",
  //       label: "Vitamin C",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-a",
  //       label: "Vitamin A",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-e",
  //       label: "Vitamin E",
  //     },
  //     {
  //       variant: "default",
  //       value: "vitamin-k2",
  //       label: "Vitamin K2",
  //     },
  //     {
  //       variant: "default",
  //       value: "probiotics",
  //       label: "Probiotics",
  //     },
  //     {
  //       variant: "default",
  //       value: "magnesium",
  //       label: "Magnesium",
  //     },
  //     {
  //       variant: "default",
  //       value: "adaptogens-herbal-supplements",
  //       label: "Adaptogens / herbal supplements",
  //     },
  //     {
  //       variant: "default",
  //       value: "amino-acids",
  //       label: "Amino acids",
  //     },
  //     {
  //       variant: "default",
  //       value: "general-multi",
  //       label: "General multi",
  //     },
  //     {
  //       variant: "default",
  //       value: "other",
  //       label: "Other",
  //     },
  //     {
  //       variant: "default",
  //       value: "none",
  //       label: "None",
  //     },
  //   ],
  // },
  {
    answered: false,
    handle: "knowledge",
    group: GROUP.LIFESTYLE,
    label:
      "When it comes to your knowledge of supplements, which of the following statements fits you most:",
    type: QUESTION_TYPE.SINGLE,
    optionName: "knowledge",
    options: [
      {
        variant: "default",
        value: "expert",
        label:
          "My friends come to me for advice on their health and wellness and consider me an expert.",
      },
      {
        variant: "default",
        value: "familiar",
        label:
          "I have read up on supplements and how they work but feel like I could learn more.",
      },
      {
        variant: "default",
        value: "none",
        label:
          "I don’t know much about supplements other than they are good for you.",
      },
    ],
  },
  // {
  //   answered: false,
  //   handle: "accomplish",
  //   group: GROUP.LIFESTYLE,
  //   label: "When choosing a supplement,you hope to:",
  //   type: QUESTION_TYPE.SINGLE,
  //   optionName: "accomplish",
  //   options: [
  //     {
  //       variant: "default",
  //       value: "routine",
  //       label: "Build an overall healthier routine to nourish my body",
  //     },
  //     {
  //       variant: "default",
  //       value: "learn",
  //       label: "Learn how supplements can help me feel better overall ",
  //     },
  //     {
  //       variant: "default",
  //       value: "solution",
  //       label: "Solve a specific ailment, condition and/or deficiency",
  //     },
  //   ],
  // },
  // {
  //   answered: false,
  //   handle: "conditions",
  //   group: GROUP.LIFESTYLE,
  //   label: "Do you have any hormonal or auto-immune conditions?",
  //   type: QUESTION_TYPE.SINGLE,
  //   optionName: "conditions",
  //   options: [
  //     {
  //       variant: "default",
  //       value: "pcos",
  //       label: "PCOS",
  //     },
  //     {
  //       variant: "default",
  //       value: "endometriosis",
  //       label: "Endometriosis",
  //     },
  //     {
  //       variant: "default",
  //       value: "hormone-imbalance",
  //       label: "General hormone imbalance",
  //     },
  //     {
  //       variant: "default",
  //       value: "prenatal-postnatal-nursing",
  //       label: "Prenatal / postnatal / nursing",
  //     },
  //     {
  //       variant: "default",
  //       value: "thyroid-disorder",
  //       label: "Thyroid disorder",
  //     },
  //     {
  //       variant: "default",
  //       value: "menopause",
  //       label: "Menopause",
  //     },
  //     {
  //       variant: "default",
  //       value: "fibromyalgia",
  //       label: "Fibromyalgia",
  //     },
  //     {
  //       variant: "default",
  //       value: "cystic-acne",
  //       label: "Cystic acne",
  //     },
  //     {
  //       variant: "default",
  //       value: "none",
  //       label: "None",
  //     },
  //     {
  //       variant: "default",
  //       value: "other",
  //       label: "Other",
  //     },
  //   ],
  // },
  {
    answered: false,
    handle: "travel",
    group: GROUP.LIFESTYLE,
    label: "How often do you travel?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "travel",
    options: [
      {
        variant: "default",
        value: "once-per-month",
        label: "Once a month or more",
      },
      {
        variant: "default",
        value: "every-other-month",
        label: "Once every other month",
      },
      {
        variant: "default",
        value: "few-times-per-year",
        label: "A few times a year",
      },
      {
        variant: "default",
        value: "rarely",
        label: "I rarely travel",
      },
    ],
  },
  {
    answered: false,
    handle: "sunshine",
    group: GROUP.LIFESTYLE,
    label: "Your typical day includes:",
    type: QUESTION_TYPE.SINGLE,
    optionName: "sunshine",
    options: [
      {
        variant: "default",
        value: "lots",
        label: "Lots of sunshine",
      },
      {
        variant: "default",
        value: "little",
        label: "Little sunshine",
      },
    ],
  },
  {
    answered: false,
    handle: "servings",
    group: GROUP.LIFESTYLE,
    label: "How many servings of fruits and vegetables do you get daily?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "servings",
    options: [
      {
        variant: "default",
        value: "none",
        label: "None",
      },
      {
        variant: "default",
        value: "1-2",
        label: "1-2 servings",
      },
      {
        variant: "default",
        value: "3-4",
        label: "3-4 servings",
      },
      {
        variant: "default",
        value: "5+",
        label: "5+ servings",
      },
    ],
  },
  {
    answered: false,
    handle: "importance",
    group: GROUP.LIFESTYLE,
    label:
      "How important is it to you that your vitamins and minerals are derived from whole food without additives and fillers?",
    type: QUESTION_TYPE.SINGLE,
    optionName: "importance",
    options: [
      {
        variant: "default",
        value: "top",
        label: "It’s my top priority",
      },
      {
        variant: "default",
        value: "somewhat",
        label: "It’s somewhat important",
      },
      {
        variant: "default",
        value: "none",
        label: "Not important at all",
      },
    ],
  },
  {
    answered: false,
    handle: "food-synergy",
    group: GROUP.LIFESTYLE,
    label: "Let's talk about<br />food synergy",
    type: QUESTION_TYPE.TIMED,
    // duration: 4000,
    content: {
      image: `<img
            class="object-cover w-full h-full mb-6 rounded-xl"
            src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/3e48ffb65a922620e571c9b4d6af75d9.jpg?v=1740600341"
            alt=""
          >`,
      copy: "At GEM, we believe in the power of whole foods over isolated supplements. Our Bites are designed with real, nutrient-dense ingredients dervied from real food ingredients that naturally contain co-factors, co-nutrients, and phytonutrients that work together synergistically. This food-first approach  enhances absorption and supports holistic health in a way that isolated supplements simply can’t replicate.",
    },
  },
  {
    answered: false,
    handle: "top-3-goals",
    group: GROUP.GOALS,
    label: "What are your top 3 health goals?",
    type: QUESTION_TYPE.MULTIPLE,
    min: 1,
    max: 3,
    optionName: "importance",
    options: [
      {
        variant: "default",
        value: "sleep",
        label: "Sleep",
        // questions: [
        //   {
        //     answered: false,
        //     handle: "sleep-concerns",
        //     group: GROUP.SLEEP,
        //     label: "What are your concerns with sleep?",
        //     type: QUESTION_TYPE.SINGLE,
        //     optionName: "sleep_concerns",
        //     options: [
        //       {
        //         variant: "default",
        //         value: "falling-asleep",
        //         label: "Falling asleep",
        //       },
        //       {
        //         variant: "default",
        //         value: "staying-asleep",
        //         label: "Staying asleep",
        //       },
        //       {
        //         variant: "default",
        //         value: "sleep-quality",
        //         label: "Getting a restful, quality sleep",
        //       },
        //     ],
        //   },
        //   {
        //     answered: false,
        //     handle: "sleep-cause",
        //     group: GROUP.SLEEP,
        //     label:
        //       'Finish this sentence: "When I have trouble sleeping, it\'s usually because..."',
        //     type: QUESTION_TYPE.SINGLE,
        //     optionName: "sleep_cause",
        //     options: [
        //       {
        //         variant: "default",
        //         value: "light-sleeper",
        //         label: "I'm a light sleeper — noise or light wake me easily.",
        //       },
        //       {
        //         variant: "default",
        //         value: "anxious-sleeper",
        //         label:
        //           "I am anxious and can't stop thinking about what needs to get done the next day.",
        //       },
        //       {
        //         variant: "default",
        //         value: "unsure",
        //         label: "I don't know why I have trouble sleeping.",
        //       },
        //     ],
        //   },
        // ],
      },
      {
        variant: "default",
        value: "mood-stress",
        label: "Mood",
        questions: [
          {
            answered: false,
            handle: "stress-affect",
            group: GROUP.MOOD_STRESS,
            label: "How does stress affect you?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "stress_affect",
            options: [
              {
                variant: "default",
                value: "no-sleep",
                label: "Can’t sleep",
              },
              {
                variant: "default",
                value: "digestion",
                label: "Poor digestion",
              },
              {
                variant: "default",
                value: "anxiety",
                label: "General anxiety",
              },
              {
                variant: "default",
                value: "inflammation",
                label: "Inflammation",
              },
              {
                variant: "default",
                value: "mood",
                label: "Poor mood",
              },
              {
                variant: "default",
                value: "other",
                label: "Other",
              },
              {
                variant: "default",
                value: "all-above",
                label: "All of the above",
              },
            ],
          },
        ],
      },
      {
        variant: "default",
        value: "hair-skin-nails",
        label: "Beauty (Hair/Skin/Nails)",
        questions: [
          {
            answered: false,
            handle: "stress-affect",
            group: GROUP.HAIR_SKIN_NAILS,
            label:
              "What are your top concerns when it comes to your hair, skin, or nails?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "stress_affect",
            options: [
              {
                variant: "default",
                value: "wrinkles",
                label: "Fine lines and wrinkles",
              },
              {
                variant: "default",
                value: "skin-health",
                label: "General skin health",
              },
              {
                variant: "default",
                value: "hair-loss",
                label: "Hair loss",
              },
              {
                variant: "default",
                value: "nail-strength",
                label: "Nail growth / strength",
              },
              {
                variant: "default",
                value: "other",
                label: "Other",
              },
            ],
          },
        ],
      },
      {
        variant: "default",
        value: "brain-cognition",
        label: "Brain & Cognition",
        questions: [
          {
            answered: false,
            handle: "concerns",
            group: GROUP.BRAIN_COGNITION,
            label:
              "What are your top concerns when it comes to brain & cognition?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "concerns",
            options: [
              {
                variant: "default",
                value: "memory",
                label: "Memory",
              },
              {
                variant: "default",
                value: "focus",
                label: "Focus",
              },
              {
                variant: "default",
                value: "cognition",
                label: "Cognition",
              },
              {
                variant: "default",
                value: "energy",
                label: "Energy",
              },
              {
                variant: "default",
                value: "other",
                label: "Other",
              },
            ],
          },
        ],
      },
      {
        variant: "default",
        value: "fitness",
        label: "Fitness",
        questions: [
          {
            answered: false,
            handle: "top-priority",
            group: GROUP.FITNESS,
            label: "What is your top priority when it comes to fitness?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "top_priority",
            options: [
              {
                variant: "default",
                value: "preworkout-snack",
                label: "A pre-workout snack",
              },
              {
                variant: "default",
                value: "recovery-support",
                label: "Recovery support",
              },
              {
                variant: "default",
                value: "stamina-endurance",
                label: "Boosting stamina & endurance",
              },
              {
                variant: "default",
                value: "weightloss",
                label: "Losing weight",
              },
              {
                variant: "default",
                value: "other",
                label: "Other",
              },
            ],
          },
          // {
          //   answered: false,
          //   handle: "exercise-frequency",
          //   group: GROUP.FITNESS,
          //   label: "On an average week, how often do you exercise?",
          //   type: QUESTION_TYPE.SINGLE,
          //   optionName: "exercise_frequency",
          //   options: [
          //     {
          //       variant: "default",
          //       value: "5-or-more",
          //       label: "5x or more",
          //     },
          //     {
          //       variant: "default",
          //       value: "3-5",
          //       label: "3-5x",
          //     },
          //     {
          //       variant: "default",
          //       value: "1-3",
          //       label: "1-3x",
          //     },
          //     {
          //       variant: "default",
          //       value: "never",
          //       label: "Never",
          //     },
          //   ],
          // },
          // {
          //   answered: false,
          //   handle: "preworkout-interest",
          //   group: GROUP.FITNESS,
          //   label: "Are you interested in a pre-workout Bite?",
          //   type: QUESTION_TYPE.SINGLE,
          //   optionName: "preworkout_interest",
          //   options: [
          //     {
          //       variant: "square",
          //       value: "yes",
          //       label: "Yes",
          //     },
          //     {
          //       variant: "square",
          //       value: "no",
          //       label: "No",
          //     },
          //   ],
          // },
        ],
      },
      {
        variant: "default",
        value: "energy",
        label: "Energy",
        questions: [
          {
            answered: false,
            handle: "concerns",
            group: GROUP.ENERGY,
            label: "What are your energy concerns?",
            type: QUESTION_TYPE.MULTIPLE,
            min: 1,
            max: Infinity,
            optionName: "concerns",
            options: [
              {
                variant: "default",
                value: "focus-clarity",
                label: "Focus & clarity",
              },
              {
                variant: "default",
                value: "metabolic",
                label: "Metabolic energy",
              },
              {
                variant: "default",
                value: "emotional",
                label:
                  'Emotional energy<br><span class="text-sm block text-section-body/30">(mood & stress)</span>',
              },
              {
                variant: "default",
                value: "physical",
                label:
                  'Physical energy<br><span class="text-sm block text-section-body/30">(stamina & endurance)</span>',
              },
              {
                variant: "default",
                value: "general",
                label: "General fatigue",
              },
            ],
          },
          // {
          //   answered: false,
          //   handle: "energy-boost-time",
          //   group: GROUP.ENERGY,
          //   label:
          //     "What time of day do you usually feel you need an energy boost?",
          //   type: QUESTION_TYPE.SINGLE,
          //   optionName: "energy_boost_time",
          //   options: [
          //     {
          //       variant: "default",
          //       value: "morning",
          //       label: "Morning",
          //     },
          //     {
          //       variant: "default",
          //       value: "midday",
          //       label: "Midday",
          //     },
          //     {
          //       variant: "default",
          //       value: "afternoon",
          //       label: "Afternoon",
          //     },
          //     {
          //       variant: "default",
          //       value: "evening",
          //       label: "Evening",
          //     },
          //   ],
          // },
          // {
          //   answered: false,
          //   handle: "energy-boost-use",
          //   group: GROUP.ENERGY,
          //   label: "What do you typically use to boost your energy?",
          //   type: QUESTION_TYPE.SINGLE,
          //   optionName: "energy_boost_use",
          //   options: [
          //     {
          //       variant: "default",
          //       value: "coffee-tea",
          //       label: "Coffee or tea",
          //     },
          //     {
          //       variant: "default",
          //       value: "energy-drinks",
          //       label: "Energy drinks",
          //     },
          //     {
          //       variant: "default",
          //       value: "supplements",
          //       label: "Supplements",
          //     },
          //     {
          //       variant: "default",
          //       value: "physical-activity",
          //       label: "Physical activity",
          //     },
          //   ],
          // },
        ],
      },
      {
        variant: "default",
        value: "immunity",
        label: "Immunity",
        //   questions: [
        //     {
        //       answered: false,
        //       handle: "sick-frequency",
        //       group: GROUP.IMMUNITY,
        //       label: "How often do you get sick per year?",
        //       type: QUESTION_TYPE.SINGLE,
        //       optionName: "sick_frequency",
        //       options: [
        //         {
        //           variant: "default",
        //           value: "1-2",
        //           label: "1-2x",
        //         },
        //         {
        //           variant: "default",
        //           value: "3-4",
        //           label: "3-4x",
        //         },
        //         {
        //           variant: "default",
        //           value: "5-6",
        //           label: "5-6x",
        //         },
        //         {
        //           variant: "default",
        //           value: "7-or-more",
        //           label: "7x or more",
        //         },
        //       ],
        //     },
        //     {
        //       answered: false,
        //       handle: "concerns",
        //       group: GROUP.IMMUNITY,
        //       label: "What are your immunity concerns?",
        //       type: QUESTION_TYPE.SINGLE,
        //       optionName: "concerns",
        //       options: [
        //         {
        //           variant: "default",
        //           value: "immune-strength",
        //           label: "General immune function and strength",
        //         },
        //         {
        //           variant: "default",
        //           value: "immunodeficiency",
        //           label: "An immunodeficiency disorder",
        //         },
        //         {
        //           variant: "default",
        //           value: "overactive",
        //           label: "Inflammation caused by overactive immune system",
        //         },
        //         {
        //           variant: "default",
        //           value: "reduce-sick-days",
        //           label: "Reducing number of sick days",
        //         },
        //       ],
        //     },
        //   ],
        // },
        // {
        //   variant: "default",
        //   value: "bone-joint",
        //   label: "Bone + joint health",
        //   questions: [
        //     {
        //       answered: false,
        //       handle: "concerns",
        //       group: GROUP.BONE_JOINT,
        //       label: "What are your bone +joint health concerns?",
        //       type: QUESTION_TYPE.SINGLE,
        //       optionName: "concerns",
        //       options: [
        //         {
        //           variant: "default",
        //           value: "density",
        //           label: "Bone density",
        //         },
        //         {
        //           variant: "default",
        //           value: "osteoporosis",
        //           label: "Osteoporosis",
        //         },
        //         {
        //           variant: "default",
        //           value: "bone-disease",
        //           label: "Bone disease",
        //         },
        //         {
        //           variant: "default",
        //           value: "healthy-aging",
        //           label: "General healthy aging",
        //         },
        //         {
        //           variant: "default",
        //           value: "existing-condition",
        //           label:
        //             'An existing condition<br><span class="text-sm block text-section-body/30">(i.e. Chrohn’s or celiac disease)</span>',
        //         },
        //         {
        //           variant: "default",
        //           value: "other",
        //           label: "Other",
        //         },
        //       ],
        //     },
        //   ],
      },
      {
        variant: "default",
        value: "gut-health",
        label: "Gut health",
        questions: [
          {
            answered: false,
            handle: "concerns",
            group: GROUP.GUT_HEALTH,
            label: "What are your gut health concerns?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "concerns",
            options: [
              {
                variant: "default",
                value: "metabolism",
                label: "Faster metabolism",
              },
              {
                variant: "default",
                value: "digestion",
                label: "General digestion",
              },
              {
                variant: "default",
                value: "inflammation",
                label: "Inflammation",
              },
              {
                variant: "default",
                value: "bloating",
                label: "General bloating",
              },
              {
                variant: "default",
                value: "sugar-cravings",
                label: "Sugar cravings",
              },
              {
                variant: "default",
                value: "regularity",
                label: "Regularity",
              },
            ],
          },
        ],
      },
      {
        variant: "default",
        value: "metabolism",
        label: "Metabolism & Weight Loss",
        questions: [
          {
            answered: false,
            handle: "concerns",
            group: GROUP.METABOLISM,
            label:
              "Do you have any of these concerns when it comes to metabolism & weight loss?",
            type: QUESTION_TYPE.SINGLE,
            optionName: "concerns",
            options: [
              {
                variant: "default",
                value: "general-digestion",
                label: "General Digestion",
              },
              {
                variant: "default",
                value: "energy-crashes",
                label: "Reducing energy crashes",
              },
              {
                variant: "default",
                value: "appetite-satiation",
                label: "Support appetite satiation",
              },
            ],
          },
        ],
      },
      {
        variant: "default",
        value: "overall-health",
        label: "General Wellness",
      },
    ],
  },
  {
    answered: false,
    handle: "educational",
    group: GROUP.GOALS,
    label: `94%<br /><small class="p mb-0 block">of GEM members reported feeling positive benefits after taking GEM for at least 4 weeks*</small>`,
    type: QUESTION_TYPE.TIMED,
    // duration: 4000,
    content: {
      image: `<img
            class="object-cover w-full h-full mb-6 rounded-xl"
            src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/3e48ffb65a922620e571c9b4d6af75d9.jpg?v=1740600341"
            alt=""
          >`,
      copy: "*based on a survey of 324 current GEM customers evaluating self-perceived efficacy",
    },
  },
  {
    answered: false,
    group: GROUP.GOALS,
    handle: "additional-context",
    type: QUESTION_TYPE.DYNAMIC,
  },
  {
    answered: false,
    handle: "answers",
    group: GROUP.SUBMIT,
    label: "Save 15% on your<br />first shipment of GEM",
    type: QUESTION_TYPE.SUBMIT,
    content: {
      image: `<img
                class="object-cover w-full h-full mb-6 rounded-xl"
                src="https://cdn.shopify.com/s/files/1/0002/5673/7286/files/Unboxing.png?v=1741217399"
                alt="Daily GEM Unboxing"
              />`,
      copy: "Enter your email to see your results and receive <strong>15% off</strong> your first order",
    },
  },
];

const [quiz, setQuiz] = createStore({
  questions: QUESTIONS.map((question, index) => ({ ...question, index })),
  activeIndex: -1,
  answers: {},
  dynamicQuestions: QUESTIONS.reduce((groups, question) => {
    if (!question.options) return groups;
    if (!question.options.some((option) => option?.questions?.length)) {
      return groups;
    }
    return {
      ...groups,
      [question.group.handle]: {
        questions: question.options
          .flatMap((option) => option?.questions || [])
          .map((question, index) => ({ ...question, index })),
        chosen: [],
        activeIndex: 0,
      },
    };
  }, {}),
});

const QuizContext = createContext(null);
const QuestionContext = createContext(null);

function useQuiz() {
  const context = useContext(QuizContext);
  if (!context)
    throw new Error("QuizContext must be used within a QuizProvider");
  return context;
}

function useQuestion() {
  const context = useContext(QuestionContext);
  if (!context)
    throw new Error("QuestionsContext must be used within a BundleProvider");
  return context;
}

function useActiveQuestion() {
  const location = useLocation();
  return createMemo(() => getActiveQuestionFromPathname(location.pathname));
}

function useQuizParams() {
  const location = useLocation();
  return () => {
    const [rootGroup, rootHandle, group, handle] = location.pathname
      .substring(1)
      .split("/");

    return {
      rootGroup,
      rootHandle,
      group,
      handle,
    };
  };
}

function usePreviousQuestion() {
  const [quiz] = useQuiz();
  const question = useActiveQuestion();
  const quizParams = useQuizParams();
  return () => {
    const prevIndex = question()?.index - 1;
    const { rootGroup, rootHandle, group, handle } = quizParams();
    if (prevIndex < 0 && (!group || !handle)) return null;
    if (prevIndex < 0) {
      const dynamicQuestion = quiz.questions.find(
        (q) => q.group.handle === rootGroup && q.handle === rootHandle,
      );
      if (!dynamicQuestion) return null;
      return quiz.questions[dynamicQuestion.index - 1];
    }
    if (!group && !handle) return quiz.questions[prevIndex];
    return quiz.dynamicQuestions[rootGroup]?.chosen[prevIndex];
  };
}

function usePreviousQuestionHref() {
  const question = usePreviousQuestion();
  const quizParams = useQuizParams();
  return () => {
    const q = question();
    const { rootGroup, rootHandle, group, handle } = quizParams();
    if (!q) return null;
    if (!group || !handle) return `/${q.group.handle}/${q.handle}`;
    const qExists = quiz.dynamicQuestions[rootGroup].chosen.some(
      (question) =>
        question.group.handle === q.group.handle &&
        question.handle === q.handle,
    );
    if (!qExists) return `/${q.group.handle}/${q.handle}`;
    return `/${rootGroup}/${rootHandle}/${q.group.handle}/${q.handle}`;
  };
}

function useNextQuestion() {
  const [quiz] = useQuiz();
  const question = useActiveQuestion();
  const quizParams = useQuizParams();
  return () => {
    if (!question) return null;
    const nextIndex = question()?.index + 1;
    const { rootGroup, rootHandle, group, handle } = quizParams();
    if (!group && !handle) return quiz.questions[nextIndex];
    const chosen = quiz.dynamicQuestions[rootGroup]?.chosen;
    if (nextIndex >= chosen.length) {
      const dynamicQuestion = quiz.questions.find(
        (q) => q.group.handle === rootGroup && q.handle === rootHandle,
      );
      if (!dynamicQuestion) return null;
      return quiz.questions[dynamicQuestion.index + 1];
    }
    return chosen[nextIndex];
  };
}

function useNextQuestionHref() {
  const question = useNextQuestion();
  const [quiz] = useQuiz();
  const quizParams = useQuizParams();
  return () => {
    const q = question();
    const { rootGroup, rootHandle, group, handle } = quizParams();
    if (!q) return null;
    if (!group || !handle) return `/${q.group.handle}/${q.handle}`;
    const qExists = quiz.dynamicQuestions[rootGroup].chosen.some(
      (question) =>
        question.group.handle === q.group.handle &&
        question.handle === q.handle,
    );
    if (!qExists) return `/${q.group.handle}/${q.handle}`;
    return `/${rootGroup}/${rootHandle}/${q.group.handle}/${q.handle}`;
  };
}

function QuizProvider(props) {
  return html`
    <${QuizContext.Provider} value=${[quiz, setQuiz]}>
      ${() => props.children}
    <//>
  `;
}

function getActiveQuestionFromPathname(pathname) {
  const [rootGroup, rootHandle, group, handle] = pathname
    .substring(1)
    .split("/");

  if (!group || !handle) {
    return quiz.questions.find((question) => {
      return (
        question.group.handle === rootGroup && question.handle === rootHandle
      );
    });
  }

  return quiz.dynamicQuestions[rootGroup].chosen.find(
    (question) => question.handle === handle && question.group.handle === group,
  );
}

function QuizProgress() {
  const [quiz] = useQuiz();

  let ref = null;

  const totalQuestions = createMemo(() => {
    return (
      quiz.questions.length +
      Object.values(quiz.dynamicQuestions).reduce(
        (acc, group) => acc + group.chosen.length,
        0,
      )
    );
  });

  const totalAnsweredQuestions = createMemo(() => {
    return (
      quiz.questions.filter((question) => question.answered).length +
      Object.values(quiz.dynamicQuestions).reduce(
        (acc, group) =>
          acc + group.chosen.filter((question) => question.answered).length,
        0,
      )
    );
  });

  const percentageQuestionsAnswered = () => {
    return totalAnsweredQuestions() / totalQuestions();
  };

  createEffect(() => {
    if (!ref) return;
    const r = percentageQuestionsAnswered();
    const initialValue = ref.value;

    const animation = animate(initialValue, r, {
      type: "spring",
      damping: 15,
      onUpdate: (latest) => {
        ref.value = latest;
      },
    });

    return onCleanup(() => {
      animation.complete();
    });
  });

  return html`
    <progress
      ref=${(el) => (ref = el)}
      class="w-full flex relative h-2 quiz__progress-bar"
      value="0"
    ></progress>
  `;
}

function QuizHeader() {
  const meta = useMeta();
  const location = useLocation();
  const goHome = (e) => {
    e.preventDefault();
    window.location.href = "/";
  };

  const isStart = () => location.pathname === "/";

  const gridClass = () => (isStart() ? "grid-cols-12" : "grid-cols-12");

  return html`
    <header
      class="sticky w-full top-0 left-0 right-0 z-50 bg-section-background border-b border-section-body/30"
    >
      <div
        class=${() =>
          `grid ${gridClass()} px-4 py-2 tablet:px-6 tablet:py-6 gap-4`}
      >
        <${Show} when=${meta?.logo}>
          <div class="col-span-2 tablet:col-span-4 flex items-center">
            <a
              href="/"
              innerHTML=${meta?.logo}
              class="block no-underline"
              onClick=${(e) => goHome(e)}
            />
          </div>
        <//>

        <div
          class="flex items-center justify-center col-span-8 tablet:col-span-4"
        >
          <${Show} when=${() => location.pathname !== "/"}>
            <p class="text-sm leading-none mb-0">${meta?.page?.title}</p>
          <//>
        </div>
        <div class="flex items-center justify-end col-span-2 tablet:col-span-4">
          <a
            href="/"
            class="h-8 w-8 flex items-center justify-center no-underline"
            onClick=${(e) => goHome(e)}
          >
            <i class="fa-solid fa-xmark"></i>
          </a>
        </div>
      </div>
      <${Show} when=${() => !isStart()}>
        <${QuizProgress} />
      <//>
    </header>
  `;
}

function QuestionProvider(props) {
  const [quiz, setQuiz] = useQuiz();
  const quizParams = useQuizParams();

  const question = createMemo(() => {
    const { rootGroup, group, handle } = quizParams();
    const index = props.index;
    if (!group || !handle) return quiz.questions[index];
    if (!quiz.dynamicQuestions[rootGroup]) return quiz.question[index];
    return quiz.dynamicQuestions[rootGroup].chosen[index];
  });

  function setQuestion(...args) {
    const { rootGroup, group, handle } = quizParams();
    if (!group || !handle) return setQuiz("questions", props.index, ...args);
    return setQuiz(
      "dynamicQuestions",
      rootGroup,
      "chosen",
      props.index,
      ...args,
    );
  }

  return html`
    <${QuestionContext.Provider} value=${() => [question(), setQuestion]}>
      ${() => props.children}
    <//>
  `;
}

function Disclaimer(props) {
  const metadata = useMeta();

  const baseClasses = "prose prose-p:text-sm prose-a:text-sm";

  const classes = () =>
    props.class ? `${baseClasses} ${props.class}` : baseClasses;

  return html`
    <${Show} when=${metadata?.disclaimer}>
      <div class=${classes} innerHTML=${metadata.disclaimer} />
    <//>
  `;
}

function QuestionLabel(props) {
  const [question] = useQuestion();
  const [quiz] = useQuiz();
  return html`
    <h2
      class=${() => props.class || "text-4xl tablet:text-6xl"}
      innerHTML=${() => replacePlaceholder(question.label, quiz.answers)}
    />
  `;
}

function QuizStart() {
  const metadata = useMeta();
  const [quiz] = useQuiz();

  const href = () => {
    const firstQuestion = quiz.questions.at(0);
    return `/${firstQuestion.group.handle}/${firstQuestion.handle}`;
  };

  return html`
    <div class="w-full min-h-screen h-screen text-center">
      <div class="grid tablet:grid-cols-2 h-full">
        <div class="col-span-1">
          <div
            class="flex items-start tablet:items-center justify-center h-full relative z-10 px-8 py-16 tablet:p-0"
          >
            <div class="max-w-xl text-left">
              <${Show} when=${metadata?.page?.title}>
                <h1 class="text-[#FFFEF0] tablet:text-section-body mb-4">
                  ${metadata.page.title}
                </h1>
              <//>
              <${Show} when=${metadata?.page?.content}>
                <div
                  class="prose [&>p]:text-[#FFFEF0] [&>p]:tablet:text-section-body mb-4 mt-[20rem] tablet:mt-0"
                  innerHTML=${metadata.page.content}
                />
              <//>
              <p class="mb-0 text-center tablet:text-left">
                <${A} href=${href} class="button button--primary"
                  >Get Started<//
                >
              </p>
            </div>
          </div>
          <div
            class="[&>img]:object-cover [&>img]:h-full [&>img]:w-full overflow-hidden w-full h-full tablet:hidden top-0 right-0 bottom-0 left-0 absolute"
            innerHTML=${metadata?.page?.featured_image}
          />
        </div>
        <div
          class="col-span-1 min-h-full max-h-screen h-screen relative hidden tablet:block"
        >
          <div
            class="absolute bottom-0 left-0 right-0 px-16 pb-16 flex items-center justify-center"
          >
            <${Disclaimer}
              class="[&_:where(a,p)]:!text-white [&_a]:border-white text-left max-w-[475px]"
            />
          </div>
          <${Show} when=${metadata?.page?.featured_image}>
            <div
              class="[&>img]:object-cover [&>img]:h-full [&>img]:w-full overflow-hidden w-full h-full"
              innerHTML=${metadata?.page?.featured_image}
            />
          <//>
        </div>
      </div>
    </div>
  `;
}

function GroupIndicator() {
  const [quiz] = useQuiz();
  const [question] = useQuestion();
  const quizParams = useQuizParams();

  const validQuestionTypes = [
    QUESTION_TYPE.SELECT,
    QUESTION_TYPE.SINGLE,
    QUESTION_TYPE.TEXT_INPUT,
    QUESTION_TYPE.MULTIPLE,
  ];

  const groupQuestions = createMemo(() => {
    const { rootGroup, group, handle } = quizParams();
    if (!group || !handle)
      return quiz.questions.reduce((group, q) => {
        if (
          q.group.handle !== question.group.handle ||
          !validQuestionTypes.includes(q.type)
        )
          return group;
        return [...group, q];
      }, []);
    return quiz.dynamicQuestions[rootGroup].chosen.reduce((group, q) => {
      if (
        q.group.handle !== question.group.handle ||
        !validQuestionTypes.includes(q.type)
      )
        return group;
      return [...group, q];
    }, []);
  });

  const totalGroupQuestions = () => groupQuestions()?.length;

  const activeGroupQuestionIndex = () =>
    groupQuestions().findIndex((gq) => gq === question) + 1;

  return html`
    <${Show} when=${() => activeGroupQuestionIndex() !== 0}>
      <div>
        <p class="mb-0 text-sm font-bold uppercase">
          ${question?.group?.label}
        </p>
        <p class="text-sm text-gray-400 uppercase">
          ${activeGroupQuestionIndex}/${totalGroupQuestions}
        </p>
      </div>
    <//>
  `;
}

function replacePlaceholder(input, obj) {
  return input.replace(/\{([^}]+)\}/g, (_, keyPath) => {
    const keys = keyPath.split(".");
    const value = keys.reduce(
      (acc, key) => (acc && acc[key] !== undefined ? acc[key] : null),
      obj,
    );
    return value !== null ? value : `{${keyPath}}`; // Keep original placeholder if value is not found
  });
}

function stripHTML(input) {
  return input.replace(/<\/?[^>]+(>|$)/g, "");
}

const TextInputQuestionRoute = (props) => {
  const [quiz, setQuiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const navigate = useNavigate();
  const nextHref = useNextQuestionHref();

  createEffect(() => {
    batch(() => {
      if (!question) return;
      setQuestion("answered", !!question.field.value);
      if (!quiz?.answers[question.group.handle]) {
        setQuiz("answers", question.group.handle, {});
      }
      setQuiz("answers", question.group.handle, question.field.name, {
        values: [
          {
            value: question.field.value,
            label: question.field.label,
          },
        ],
        question: question.label,
      });
    });
  });

  function handleNext(e) {
    e.preventDefault();
    if (!question.answered) return;
    if (!nextHref()) return;
    navigate(nextHref());
  }

  let inputRef = null;

  onMount(() => {
    if (!inputRef) return;
    inputRef.focus();
  });

  return html`
    <div class="flex justify-center w-full min-h-[90vh] px-8 py-24 text-center">
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full max-w-xl mx-auto">
          <div>
            <${GroupIndicator} />
            <${QuestionLabel} class="text-4xl tablet:text-6xl" />
            <form class="relative mt-28" onSubmit=${(e) => handleNext(e)}>
              <button
                disabled=${() => !question.answered}
                class="absolute top-0 right-0 text-xl button button--primary button--sm"
              >
                <i class="fa-regular fa-arrow-right-long"> </i>
              </button>
              <input
                type="text"
                name=${() => question.field.name}
                aria-label=${() => question.field.label}
                value=${() => question.field.value}
                onInput=${(e) => setQuestion("field", "value", e.target.value)}
                class="block w-full px-3 py-4 text-base text-gray-900 bg-transparent border-0 border-b-2 placeholder:text-gray-400 sm:text-sm/6 focus:outline-none focus:ring-0 focus:border-section-body"
                placeholder=${() => question.field.label}
                ref=${(el) => (inputRef = el)}
              />
            </form>
          </div>
          <${Disclaimer} class="text-left tablet:text-center" />
        </div>
      </div>
    </div>
  `;
};

const TimedQuestionRoute = (props) => {
  const [question, setQuestion] = useQuestion();
  const navigate = useNavigate();
  const nextHref = useNextQuestionHref();

  createEffect(() => {
    setQuestion("answered", true);
    if (question.duration !== undefined) {
      const timer = setTimeout(() => {
        navigate(nextHref());
      }, question.duration);
      return onCleanup(() => clearTimeout(timer));
    }
  });

  return html`
    <div
      class="flex justify-center w-full min-h-[90vh] tablet:min-h-0 py-12 tablet:py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex h-full items-center">
          <${Switch}>
            <${Match} when=${() => question.variant === "icon"}>
              <div class="max-w-xl mx-auto flex flex-col">
                <${GroupIndicator} />
                <${QuestionLabel}
                  class="text-4xl tablet:text-6xl mb-20 order-2 tablet:order-1"
                />
                <${Show} when=${() => question?.content?.image}>
                  <div
                    class="flex justify-center items-center order-1 tablet:order-2 mb-6 tablet:mb-0"
                    innerHTML=${() => question.content.image}
                  />
                <//>
              </div>
            <//>
            <${Match} when=${() => question?.variant !== "icon"}>
              <div class="max-w-[365px] mx-auto">
                <${GroupIndicator} />
                <${QuestionLabel}
                  class="mb-6 text-4xl tablet:text-6xl text-left px-6"
                />
                <${Show} when=${() => question?.content?.image}>
                  <div
                    class="aspect-1 before:float-left before:pt-[100%] after:block after:clear-both"
                    innerHTML=${() => question.content.image}
                  />
                <//>
                <${Show} when=${() => question?.content?.copy}>
                  <p
                    class="mb-0 text-base [&_a]:underline text-left mt-4 px-6"
                    innerHTML=${() => question?.content?.copy}
                  />
                <//>
              </div>
            <//>
          <//>
        </div>
      </div>
    </div>
  `;
};

function Option(props) {
  const [question] = useQuestion();
  const id = () =>
    `${question.group.handle}-${question.optionName}-${props.value}`;

  function handleChange(e) {
    if (!props.onChange) return;
    props.onChange(e);
  }

  const radioClasses = () =>
    props.type === "radio" ? "peer-checked:cursor-default" : "";

  return html`
    <div class="group h-full">
      <input
        type=${() => props.type}
        id=${id}
        name=${() => question.optionName}
        value=${() => props.value}
        checked=${() => props.checked}
        onChange=${(e) => handleChange(e)}
        disabled=${() => props.disabled}
        class="sr-only peer"
      />
      <label
        for=${id}
        class=${() =>
          `p h-full w-full text-center flex flex-col leading-none justify-center items-center py-4 px-8 m-0 text-base border-[1px] border-[#301d01] rounded-md peer-[&:not(:disabled)]:[&:where(:focus,:hover)]:bg-[#EFFFC2] peer-[&:not(:disabled)]:[&:where(:focus,:hover)]:rounded-full peer-checked:bg-[#EFFFC2] peer-checked:rounded-full cursor-pointer peer-disabled:cursor-not-allowed peer-disabled:opacity-50 ${radioClasses()}`}
        innerHTML=${() =>
          props?.icon
            ? `${props.icon}<span class="mt-2">${props?.label || props.value}</span>`
            : props?.label || props.value}
      />
    </div>
  `;
}

function OptionsContainer(props) {
  const [question] = useQuestion();

  const optionsLength = () => question.options.length;
  const isOdd = () => optionsLength() % 2 !== 0;
  const isEven = () => optionsLength() % 2 === 0;
  const squareVariants = () =>
    question.options.some((option) => option.variant === "square");

  const FallbackWrapper = (props) => {
    return html`
      <div class="flex flex-wrap w-full gap-4 justify-center">
        ${() => props.children}
      </div>
    `;
  };

  return html`
    <${Switch}
      fallback=${() => html`<${FallbackWrapper}>${() => props.children}<//>`}
    >
      <${Match} when=${() => optionsLength() === 3}>
        <div class="grid grid-cols-1 gap-4 auto-rows-fr">
          ${() => props.children}
        </div>
      <//>
      <${Match} when=${() => optionsLength() > 3 && isOdd()}>
        <div class="flex flex-wrap w-full gap-4 justify-center">
          ${() => props.children}
        </div>
      <//>
      <${Match}
        when=${() => isEven() && !squareVariants() && optionsLength() >= 8}
      >
        <div class="grid grid-cols-2 gap-4 auto-rows-fr pb-24">
          ${() => props.children}
        </div>
      <//>
      <${Match}
        when=${() => isEven() && !squareVariants() && optionsLength() < 8}
      >
        <div class="grid grid-cols-1 tablet:grid-cols-2 gap-4 auto-rows-fr">
          ${() => props.children}
        </div>
      <//>
      <${Match} when=${() => squareVariants()}>
        <div class="flex flex-wrap justify-center gap-4 w-full">
          ${() => props.children}
        </div>
      <//>
    <//>
  `;
}

function OptionWrapper(props) {
  const [question] = useQuestion();

  const optionsLength = () => question.options.length;
  const isOdd = () => optionsLength() % 2 !== 0;
  const isEven = () => optionsLength() % 2 === 0;
  const squareVariants = () =>
    question.options.some((option) => option.variant === "square");

  const FallbackWrapper = (props) => {
    return html`
      <div class="grow basis-full tablet:basis-1/3 tablet:max-w-[50%]">
        ${() => props.children}
      </div>
    `;
  };

  return html`
    <${Switch}
      fallback=${() => html`<${FallbackWrapper}>${() => props.children}<//>`}
    >
      <${Match} when=${() => optionsLength() === 3}>
        <div class="col-span-1 h-full">${() => props.children}</div>
      <//>
      <${Match} when=${() => optionsLength() > 3 && isOdd()}>
        <div class="grow basis-full tablet:basis-1/3 tablet:max-w-[50%]">
          ${() => props.children}
        </div>
      <//>
      <${Match} when=${() => isEven() && !squareVariants()}>
        <div class="col-span-1 h-full">${() => props.children}</div>
      <//>
      <${Match} when=${() => squareVariants()}>
        <div
          class="grow basis-1/3 max-w-[50%] tablet:basis-1/5 tablet:max-w-[25%] justify-center gap-4 aspect-1"
        >
          ${() => props.children}
        </div>
      <//>
    <//>
  `;
}

const SingleQuestionRoute = (props) => {
  const [quiz, setQuiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const quizParams = useQuizParams();
  const navigate = useNavigate();
  const nextHref = useNextQuestionHref();

  const createOptionChangeHandler = (index) => {
    return (e) => {
      const { rootGroup, group, handle } = quizParams();
      const option = question.options[index];
      if (group && handle) {
        return batch(() => {
          setQuestion("answered", true);
          setQuestion("options", (options) => {
            return options.map((o) => ({ ...o, checked: false }));
          });
          setQuestion("options", index, "checked", e.target.checked);
          if (!quiz?.answers[rootGroup]) {
            setQuiz("answers", rootGroup, {});
          }
          if (!quiz?.answers[rootGroup][group]) {
            setQuiz("answers", rootGroup, group, {});
          }
          setQuiz("answers", rootGroup, group, question.optionName, {
            values: [
              {
                value: option.value,
                label: option.label || option.value,
              },
            ],
            question: question.label,
          });

          navigate(nextHref());
        });
      }
      return batch(() => {
        setQuestion("answered", true);
        setQuestion("options", (options) => {
          return options.map((o) => ({ ...o, checked: false }));
        });
        setQuestion("options", index, "checked", e.target.checked);
        if (!quiz?.answers[rootGroup]) {
          setQuiz("answers", rootGroup, {});
        }
        setQuiz("answers", rootGroup, question.optionName, {
          values: [
            {
              value: option.value,
              label: option.label || option.value,
            },
          ],
          question: question.label,
        });

        navigate(nextHref());
      });
    };
  };

  return html`
    <div
      class="flex justify-center w-full min-h-[90vh] py-12 tablet:py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full">
          <div class="w-full max-w-xl mx-auto">
            <div>
              <${GroupIndicator} />
              <${QuestionLabel} />
              <p class="text-base mb-8">Select one</p>
            </div>
            <${OptionsContainer}>
              <${For} each=${() => question.options}>
                ${(option, index) => {
                  return html`
                    <${OptionWrapper}>
                      <${Option}
                        onChange=${(e) => createOptionChangeHandler(index())(e)}
                        type="radio"
                        ...${() => option}
                      />
                    <//>
                  `;
                }}
              <//>
            <//>
          </div>
        </div>
      </div>
    </div>
  `;
};

const MultipleQuestionRoute = (props) => {
  const [quiz, setQuiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const quizParams = useQuizParams();

  const answers = () => question.options.filter((option) => option.checked);

  const answeredQuestionsLength = () => answers().length;

  const isDisabled = (option) =>
    !option.checked && answeredQuestionsLength() >= question.max;

  const createOptionChangeHandler = (index) => {
    return (e) => {
      const option = question.options[index];
      const { rootGroup, group, handle } = quizParams();
      if (isDisabled(option)) return;

      if (group && handle) {
        return batch(() => {
          if (!quiz?.answers[rootGroup]) {
            setQuiz("answers", rootGroup, {});
          }

          if (!quiz?.answers[rootGroup][group]) {
            setQuiz("answers", rootGroup, group, {});
          }

          let answers =
            quiz.answers[rootGroup][group][question.optionName]?.values || [];

          if (e.target.checked)
            answers = [
              ...answers,
              { value: option.value, label: option?.label || option.value },
            ];
          else
            answers = answers.filter(
              (answer) => answer.value !== e.target.value,
            );
          setQuestion(
            "answered",
            answers.length >= question.min && answers.length <= question.max,
          );
          setQuestion("options", index, "checked", e.target.checked);

          setQuiz("answers", rootGroup, group, question.optionName, {
            question: question.label,
            values: answers,
          });
        });
      }

      return batch(() => {
        if (!quiz?.answers[question.group.handle]) {
          setQuiz("answers", question.group.handle, {});
        }

        let answers =
          quiz.answers[question.group.handle][question.optionName]?.values ||
          [];

        if (e.target.checked)
          answers = [
            ...answers,
            { value: option.value, label: option?.label || option.value },
          ];
        else
          answers = answers.filter((answer) => answer.value !== e.target.value);

        const dynamicQuestions = option?.questions;

        if (dynamicQuestions) {
          setQuiz("dynamicQuestions", rootGroup, "chosen", (chosen) => {
            if (!chosen) chosen = [];
            if (e.target.checked) {
              return [...chosen, ...option.questions].map((o, index) => ({
                ...o,
                index,
              }));
            } else {
              return chosen
                .filter((question) => {
                  return option.questions.some(
                    (q) =>
                      q.group.handle !== question.group.handle &&
                      q.handle !== question.handle,
                  );
                })
                .map((o, index) => ({ ...o, index }));
            }
          });
        }

        setQuestion(
          "answered",
          answers.length >= question.min && answers.length <= question.max,
        );
        setQuestion("options", index, "checked", e.target.checked);

        setQuiz("answers", rootGroup, question.optionName, {
          question: question.label,
          values: answers,
        });
      });
    };
  };

  return html`
    <div
      class="flex justify-center w-full overflow-hidden min-h-[90vh] px-8 tablet:min-h-screen py-12 tablet:py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full">
          <div class="w-full max-w-xl mx-auto">
            <div>
              <${GroupIndicator} />
              <${QuestionLabel} />
              <${Show}
                when=${() => !Number.isFinite(question?.max || Infinity)}
              >
                <p class="text-base mb-8">Select all that apply</p>
              <//>
              <${Show} when=${() => Number.isFinite(question?.max || Infinity)}>
                <p class="text-base mb-8">Select up to ${question.max}</p>
              <//>
            </div>
            <${OptionsContainer}>
              <${For} each=${() => question.options}>
                ${(option, index) => {
                  return html`
                    <${OptionWrapper}>
                      <${Option}
                        onChange=${(e) => createOptionChangeHandler(index())(e)}
                        type="checkbox"
                        disabled=${() => isDisabled(option)}
                        ...${() => option}
                      />
                    <//>
                  `;
                }}
              <//>
            <//>
          </div>
        </div>
      </div>
    </div>
  `;
};

const SelectQuestionRoute = (props) => {
  const [quiz, setQuiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const nextHref = useNextQuestionHref();
  const navigate = useNavigate();

  const inactiveClasses = () =>
    !question.options.some((option) => option.selected)
      ? "text-gray-400"
      : "text-section-body";

  function handleChange(e) {
    batch(() => {
      const optionIndex = question.options.findIndex(
          (o) => o.value === e.target.value,
        ),
        option = question.options[optionIndex];
      setQuestion("answered", true);
      setQuestion("options", (options) => {
        return options.map((o) => ({ ...o, selected: false }));
      });
      setQuestion("options", optionIndex, "selected", true);
      if (!quiz?.answers[question.group.handle]) {
        setQuiz("answers", question.group.handle, {});
      }
      setQuiz("answers", question.group.handle, question.optionName, {
        values: [{ value: option.value, label: option.label || option.value }],
        question: question.label,
      });
    });
  }

  return html`
    <div
      class="flex justify-center w-full overflow-hidden min-h-[90vh] px-8 tablet:max-h-screen py-12 tablet:py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full max-w-xl mx-auto">
          <div>
            <${GroupIndicator} />
            <${QuestionLabel} />
            <div class="relative mt-28">
              <select
                name=${() => question.optionName}
                class=${() =>
                  `block w-full px-3 py-4 text-base bg-transparent border-0 border-b-2 sm:text-sm/6 focus:outline-0 focus:ring-0 focus:border-section-body`}
                placeholder="Choose"
                onChange=${(e) => handleChange(e)}
              >
                <option selected disabled="disabled">Choose</option>
                <${For} each=${() => question.options}>
                  ${(option, index) => {
                    return html`
                      <option
                        selected=${() => option.selected}
                        value=${() => option.value}
                      >
                        ${() => option.label}
                      </option>
                    `;
                  }}
                <//>
              </select>
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

function saveQuizResults(quiz) {
  localStorage.setItem("2025-quiz-results", JSON.stringify(quiz.answers));
}

const SubmitQuestionRoute = (props) => {
  const meta = useMeta();
  const [quiz, setQuiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const [email, setEmail] = createSignal(quiz?.answers?.email || "");

  const gotoResults = () =>
    (window.location.href = `${window.location.origin}${meta.resultsUrl}${window.location.search}`);

  const onSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    batch(() => {
      setQuiz("answers", "email", email());
      saveQuizResults(quiz);
      gotoResults();
    });

    // Handle storing quiz results and going to results page.
  };

  const onChange = (e) => {
    return batch(() => {
      setEmail(e.target.value || "");
      setQuestion("answered", e.target.value || "");
    });
  };

  return html`
    <div
      class="flex justify-center w-full overflow-hidden min-h-[90vh] tablet:max-h-screen py-12 tablet:py-24 text-center"
    >
      <div class="container flex-1">
        <div class="flex flex-col justify-between h-full">
          <div class="max-w-[365px] mx-auto">
            <${QuestionLabel} class="text-5xl mb-6 text-left" />
            <div class="aspect-1">
              <${Show} when=${() => question?.content?.image}>
                <div innerHTML=${() => question.content.image} />
              <//>
              <${Show} when=${() => question?.content?.copy}>
                <p
                  innerHTML=${() => question.content.copy}
                  class="mb-6 text-xl [&_a]:underline text-left"
                />
              <//>
              <form class="relative mb-6" onSubmit=${(e) => onSubmit(e)}>
                <input
                  type="email"
                  name="email"
                  id="email"
                  value=${email}
                  onInput=${(e) => onChange(e)}
                  aria-label="Email address"
                  autocomplete="email"
                  class="block w-full relative px-3 py-4 text-base text-section-body bg-white border-0 rounded-md placeholder:text-gray-400 sm:text-sm/6 focus:outline-0"
                  placeholder="Email address"
                />
                <button
                  type="submit"
                  disabled=${() => !question?.answered}
                  class="absolute top-1 right-1 text-xl button button--primary button--sm"
                  onClick=${(e) => question.answered && onSubmit(e)}
                >
                  <span class="sr-only">Submit results</span>
                  <i class="fa-regular fa-arrow-right-long"></i>
                </button>
              </form>
              <!--
               <p class="mb-0 text-base">
                 <a
                   href=""
                   onClick=${(e) => {
                e.preventDefault();
                saveQuizResults(quiz);
                gotoResults();
              }}
                   class="underline-offset-4 underline"
                 >
                   Skip for now
                 </a>
               </p>
               -->
            </div>
          </div>
        </div>
      </div>
    </div>
  `;
};

const DynamicQuestionRouteLayout = (props) => {
  return () => props.children;
};

const DynamicQuestionRoute = (props) => {
  const [quiz] = useQuiz();
  const [question, setQuestion] = useQuestion();
  const firstDynamicQuestion = () =>
    quiz?.dynamicQuestions[question.group.handle]?.chosen?.at(0);
  const navigate = useNavigate();
  const nextHref = useNextQuestionHref();
  createEffect(() => {
    const first = firstDynamicQuestion();
    if (!first) return navigate(nextHref());
    batch(() => {
      setQuestion("answered", true);
      navigate(
        `/${question.group.handle}/${question.handle}/${first.group.handle}/${first.handle}`,
      );
    });
  });
  return () => props.children;
};

const DynamicQuestionRouteMatcher = (props) => {
  const question = useActiveQuestion();
  return () =>
    html`<${BASIC_ROUTES[question().type]} index=${() => question()?.index} />`;
};

const BaseRouteComponent = (props) => {
  return html`
    <${QuestionProvider} index=${() => props.index}>
      ${() => props.children}
    <//>
  `;
};

function QuizNavigation() {
  const question = useActiveQuestion(),
    previousHref = usePreviousQuestionHref(),
    nextHref = useNextQuestionHref(),
    navigate = useNavigate();

  return html`
    <nav
      class="w-full fixed left-0 right-0 bottom-0 px-4 tablet:px-8 pb-8 tablet:pb-16 pt-4 tablet:pt-8 from-section-background/0 to-section-background bg-gradient-to-b"
    >
      <ul class="grid grid-cols-2 [&>*]:list-none">
        <${Show} when=${previousHref}>
          <li class="text-right tablet:text-left me-4 tablet:me-0">
            <button
              onClick=${(event) => navigate(previousHref())}
              class="text-xl button button--primary button--sm"
            >
              <span class="sr-only">Previous question</span>
              <i class="fa-regular fa-arrow-left-long"></i>
            </button>
          </li>
        <//>
        <${Show} when=${nextHref}>
          <li class="text-left tablet:text-right">
            <button
              disabled=${() => !question().answered}
              onClick=${(event) => question()?.answered && navigate(nextHref())}
              class="text-xl button button--primary button--sm"
            >
              <span class="sr-only">Next question</span>
              <i class="fa-regular fa-arrow-right-long"></i>
            </button>
          </li>
        <//>
      </ul>
    </nav>
  `;
}

function RootLayout(props) {
  const location = useLocation();
  const activeQuestion = useActiveQuestion();

  const showQuizNavigation = () => {
    return (
      location.pathname !== "/" &&
      activeQuestion()?.type !== QUESTION_TYPE.SUBMIT &&
      activeQuestion()?.type !== QUESTION_TYPE.TEXT_INPUT
    );
  };

  return html`
    <${QuizHeader} />
    <div class="relative">
      ${props.children}
      <${Show} when=${showQuizNavigation}>
        <${QuizNavigation} />
      <//>
    </div>
  `;
}

const BASIC_ROUTES = {
  [QUESTION_TYPE.TIMED]: (props) => html`
    <${BaseRouteComponent} index=${() => props.index}>
      <${TimedQuestionRoute} />
    <//>
  `,
  [QUESTION_TYPE.TEXT_INPUT]: (props) => html`
    <${BaseRouteComponent} index=${() => props.index}>
      <${TextInputQuestionRoute} />
    <//>
  `,
  [QUESTION_TYPE.MULTIPLE]: (props) => html`
    <${BaseRouteComponent} index=${() => props.index}>
      <${MultipleQuestionRoute} />
    <//>
  `,
  [QUESTION_TYPE.SINGLE]: (props) => html`
    <${BaseRouteComponent} index=${() => props.index}>
      <${SingleQuestionRoute} />
    <//>
  `,
  [QUESTION_TYPE.SELECT]: (props) => html`
    <${BaseRouteComponent} index=${() => props.index}>
      <${SelectQuestionRoute} />
    <//>
  `,
};

const routes = createMemo(() =>
  quiz.questions.reduce(
    (routes, question, index) => {
      if (question.type in BASIC_ROUTES) {
        return [
          ...routes,
          {
            path: `/${question.group.handle}/${question.handle}`,
            component: () =>
              html`<${BASIC_ROUTES[question.type]} index=${index} />`,
          },
        ];
      }

      if (question.type === QUESTION_TYPE.SUBMIT) {
        return [
          ...routes,
          {
            path: `/${question.group.handle}/${question.handle}`,
            component: () => () => html`
              <${BaseRouteComponent} index=${index}>
                <${SubmitQuestionRoute} />
              <//>
            `,
          },
        ];
      }

      if (question.type === QUESTION_TYPE.DYNAMIC) {
        return [
          ...routes,
          {
            path: `/${question.group.handle}/${question.handle}`,
            component: (props) => () => html`
              <${BaseRouteComponent} index=${index}>
                <${DynamicQuestionRouteLayout}> ${() => props.children} <//>
              <//>
            `,
            children: [
              {
                path: `/`,
                component: () => () => html`<${DynamicQuestionRoute} />`,
              },
              {
                path: `/:group/:handle`,
                component: () => () => html`<${DynamicQuestionRouteMatcher} />`,
              },
            ],
          },
        ];
      }

      return routes;
    },
    [
      {
        path: "/",
        component: () => html`<${QuizStart} />`,
      },
    ],
  ),
);

function App() {
  return html`
    <${MetaProvider}>
      <${QuizProvider}>
        <${HashRouter}
          root=${() => (props) => html`<${RootLayout} ...${props} />`}
          explicitLinks=${true}
          preload=${false}
        >
          ${routes}
        <//>
      <//>
    <//>
  `;
}

render(() => html`<${App} />`, document.getElementById("app"));
